/**
 * Typed access to the CONSUMER glossary.
 *
 * `generated/glossary.generated.ts` is written by `context/scripts/publish-glossary.mjs`
 * from `docs/glossary.json` and is committed. Nothing outside this module imports the
 * generated file directly: this is where the untyped artifact becomes a typed shape and
 * where lookup lives, so a change to the artifact's layout lands in one place. Same
 * arrangement as `apps/platform/src/docs/glossary.ts`, which this mirrors.
 *
 * A term is defined ONCE, in `docs/glossary.json`, and every surface reads it from there
 * (working-conventions §44). In this app the surfaces are the Help page's glossary list,
 * the `TermSheet`, and Help search. None of them retypes a definition.
 */
import { GLOSSARY_ENTRIES_CONSUMER } from './generated/glossary.generated'

/** What kind of thing is being defined. */
export type GlossaryKind = 'term' | 'column' | 'formula' | 'status'

/**
 * A cross-cutting label, orthogonal to `kind` — see the platform's `glossary.ts`.
 *
 * No consumer entry carries one today: `metric` labels the Analytics figures, which are a
 * brand surface. The type is kept here anyway, for the same reason `audience` keeps its
 * full union rather than narrowing to `'consumer'` — the two `GlossaryEntry` shapes
 * describe ONE artifact format, and letting them diverge per app is how a field ends up
 * meaning two things.
 */
export type GlossaryTag = 'metric'

export interface GlossaryEntry {
  /** Stable kebab-case id. It is the key every `(i)` affordance references. */
  id: string
  term: string
  /** Other spellings a reader might search for. Unique across the whole glossary. */
  aliases?: readonly string[]
  kind: GlossaryKind
  /**
   * The LEAST privileged reader allowed to see this entry, not a label for who it is
   * written for. The audiences nest — consumer ⊂ brand ⊂ internal — so this artifact,
   * built for the consumer floor, carries `'consumer'` and nothing else. The union is
   * kept whole rather than narrowed to `'consumer'` so the shape stays identical to the
   * platform's `GlossaryEntry` and the two cannot drift into different types for one
   * artifact format.
   */
  audience: 'consumer' | 'brand' | 'internal'
  /** ONE sentence, capped at 180 chars by the publisher. This is the sheet's body. */
  short: string
  /** Optional detail. Shown under `short`, never on its own. */
  long?: string
  /** `kind: 'formula'` only — no consumer entry uses these today. */
  formula?: string
  unit?: string
  /** Ids of related entries. May point OUTSIDE this artifact — see `relatedTerms`. */
  seeAlso?: readonly string[]
  /** `category/slug[#heading]` references into the BRAND guides. Not routable here. */
  docs?: readonly string[]
  /** Cross-cutting labels. Unused in this artifact — see `GlossaryTag`. */
  tags?: readonly GlossaryTag[]
}

export const CONSUMER_GLOSSARY = GLOSSARY_ENTRIES_CONSUMER as readonly GlossaryEntry[]

const BY_ID = new Map(CONSUMER_GLOSSARY.map(e => [e.id, e]))

/** id or alias, case-insensitive. Aliases are unique, enforced at publish time. */
const BY_ALIAS = new Map<string, GlossaryEntry>()
for (const e of CONSUMER_GLOSSARY) {
  BY_ALIAS.set(e.term.toLowerCase(), e)
  for (const a of e.aliases ?? []) BY_ALIAS.set(a.toLowerCase(), e)
}

export const glossaryTerm = (id: string): GlossaryEntry | undefined => BY_ID.get(id)

/** Resolve loose input — "Scans", "tap", "chain of ownership" — to an entry. */
export const lookupTerm = (text: string): GlossaryEntry | undefined =>
  BY_ID.get(text) ?? BY_ALIAS.get(text.trim().toLowerCase())

/**
 * `seeAlso` ids resolved to entries, DROPPING any that do not resolve.
 *
 * The drop is the audience filter, not defensive coding, and it is the only thing
 * enforcing the audience boundary at this layer.
 *
 * `seeAlso` is a graph over the WHOLE corpus, and it legitimately points upward:
 * `certificate-status-active` links to `activation-trigger`, `certificate` links to
 * `tag`, `scan-status-verified` links to `scan-status-invalid` — every one of those is a
 * brand-floor term that a consumer must never be shown. The publisher build-enforces
 * that every `seeAlso` resolves against the FULL glossary, so those ids are valid; they
 * are simply absent from this artifact, which carries the consumer floor only.
 *
 * So filtering to what resolves HERE is exactly "keep the consumer-visible half of the
 * graph". Nothing else checks it: there is no audience comparison anywhere downstream,
 * and `TermSheet` renders whatever this returns. Remove the filter and a consumer's
 * Related row starts offering terms the product deliberately does not explain to them.
 */
export const relatedTerms = (entry: GlossaryEntry): GlossaryEntry[] =>
  (entry.seeAlso ?? [])
    .map(id => BY_ID.get(id))
    .filter((e): e is GlossaryEntry => Boolean(e))
